import React, { useEffect, useState } from "react";
import TopBar from "@/components/TopBar";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Plus, Save, Trash2, Star, Quote, CalendarDays, GraduationCap, Sparkles } from "lucide-react";

const VALUE_OPTIONS = [
  { v: "", l: "— none —" },
  { v: "toughness", l: "Toughness" },
  { v: "accountability", l: "Accountability" },
  { v: "brotherhood", l: "Brotherhood" },
  { v: "relentless", l: "Relentless" },
  { v: "humility", l: "Humility" },
  { v: "standards", l: "Standards" },
];

// -------------------- Quotes --------------------
function QuoteCard({ q, onDelete, onToggleFav }) {
  return (
    <div className="bg-surface border border-surface-3 p-5 relative group" data-testid={`quote-${q.id}`}>
      <Quote className="w-5 h-5 text-amber/40 absolute top-3 right-3" />
      <div className="text-lg text-slate-100 leading-snug pr-8">"{q.text}"</div>
      <div className="mt-3 text-[10px] font-condensed uppercase tracking-[0.28em] text-amber">— {q.author}</div>
      {(q.tags || []).length > 0 && (
        <div className="flex flex-wrap gap-1 mt-3">
          {q.tags.map(t => (
            <span key={t} className="text-[9px] font-condensed uppercase tracking-widest text-slate-400 border border-surface-3 px-1.5 py-0.5">{t}</span>
          ))}
        </div>
      )}
      <div className="flex items-center justify-between mt-4 pt-3 border-t border-surface-3">
        <button onClick={() => onToggleFav(q)} data-testid={`quote-fav-${q.id}`}
          className={`text-xs font-condensed uppercase tracking-widest flex items-center gap-1 transition-colors ${q.favorite ? "text-amber" : "text-slate-500 hover:text-amber"}`}>
          <Star className="w-3 h-3" fill={q.favorite ? "#F59E0B" : "none"} /> {q.favorite ? "Favorited" : "Favorite"}
        </button>
        <button onClick={() => onDelete(q)} data-testid={`quote-del-${q.id}`}
          className="text-slate-500 hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

function QuotesPanel() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ text: "", author: "", tags: "" });
  const [creating, setCreating] = useState(false);

  const reload = async () => {
    const { data } = await api.get("/culture/quotes");
    setItems(data);
  };
  useEffect(() => { reload(); }, []);

  const create = async () => {
    if (!form.text.trim()) return;
    setCreating(true);
    try {
      await api.post("/culture/quotes", {
        text: form.text.trim(),
        author: form.author.trim() || "Unknown",
        tags: form.tags.split(",").map(s => s.trim()).filter(Boolean),
      });
      setForm({ text: "", author: "", tags: "" });
      await reload();
      toast.success("Quote added");
    } catch { toast.error("Add failed"); }
    finally { setCreating(false); }
  };

  const del = async (q) => {
    if (!window.confirm("Delete this quote?")) return;
    await api.delete(`/culture/quotes/${q.id}`);
    setItems(items.filter(x => x.id !== q.id));
  };

  const toggleFav = async (q) => {
    const next = { text: q.text, author: q.author, tags: q.tags || [], favorite: !q.favorite };
    const { data } = await api.put(`/culture/quotes/${q.id}`, next);
    setItems(items.map(x => x.id === q.id ? data : x));
  };

  return (
    <div className="space-y-6">
      {/* Add form */}
      <div className="bg-surface border border-surface-3 p-5" data-testid="quote-add-form">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="w-4 h-4 text-amber" />
          <div className="font-condensed uppercase tracking-[0.25em] text-sm">Add To Library</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-[1fr_220px_220px_auto] gap-3">
          <Input value={form.text} onChange={(e)=>setForm({...form, text:e.target.value})} placeholder="Quote text…"
            className="bg-background border-surface-3 rounded-none" data-testid="quote-input-text" />
          <Input value={form.author} onChange={(e)=>setForm({...form, author:e.target.value})} placeholder="Author"
            className="bg-background border-surface-3 rounded-none" data-testid="quote-input-author" />
          <Input value={form.tags} onChange={(e)=>setForm({...form, tags:e.target.value})} placeholder="tags, comma-separated"
            className="bg-background border-surface-3 rounded-none" />
          <Button onClick={create} disabled={creating} data-testid="quote-add-btn"
            className="rounded-none bg-amber hover:bg-amber-600 text-[#0B0D12] font-condensed uppercase tracking-widest text-xs">
            <Plus className="w-3 h-3 mr-1" /> Add
          </Button>
        </div>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="quotes-grid">
        {items.map(q => <QuoteCard key={q.id} q={q} onDelete={del} onToggleFav={toggleFav} />)}
        {items.length === 0 && <div className="text-sm text-slate-500 italic">No quotes yet. Add the first one above.</div>}
      </div>
    </div>
  );
}

// -------------------- Themes --------------------
function ThemeEditor({ theme, onSaved, onDeleted }) {
  const [t, setT] = useState(theme);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setT(prev => ({ ...prev, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        week: +t.week || 1, title: t.title, body: t.body || "",
        value_ref: (t.value_ref === "__none__" ? "" : (t.value_ref || "")),
        reflection: t.reflection || ""
      };
      const { data } = t.id ? await api.put(`/culture/themes/${t.id}`, payload) : await api.post("/culture/themes", payload);
      onSaved(data);
      toast.success("Theme saved");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };
  const del = async () => {
    if (!t.id || !window.confirm("Delete this theme?")) return;
    await api.delete(`/culture/themes/${t.id}`);
    onDeleted(t.id);
    toast.success("Deleted");
  };
  return (
    <div className="bg-surface border border-surface-3 p-5" data-testid={`theme-editor-${t.id || 'draft'}`}>
      <div className="grid grid-cols-[80px_1fr_200px] gap-3 items-end">
        <div>
          <div className="text-[10px] font-condensed uppercase tracking-widest text-slate-500">Week</div>
          <Input type="number" value={t.week || 1} onChange={(e)=>set("week", +e.target.value || 1)}
            className="mt-1 bg-background border-surface-3 rounded-none text-center font-mono" data-testid="theme-week" />
        </div>
        <div>
          <div className="text-[10px] font-condensed uppercase tracking-widest text-slate-500">Theme Title</div>
          <Input value={t.title || ""} onChange={(e)=>set("title", e.target.value)}
            className="mt-1 bg-background border-surface-3 rounded-none" data-testid="theme-title" />
        </div>
        <div>
          <div className="text-[10px] font-condensed uppercase tracking-widest text-slate-500">Tied To Value</div>
          <Select value={t.value_ref || ""} onValueChange={(v)=>set("value_ref", v)}>
            <SelectTrigger className="mt-1 bg-background border-surface-3 rounded-none"><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent className="bg-surface border-surface-3 rounded-none">
              {VALUE_OPTIONS.map(o => <SelectItem key={o.v} value={o.v || "__none__"}>{o.l}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="mt-3">
        <div className="text-[10px] font-condensed uppercase tracking-widest text-slate-500">Description</div>
        <Textarea value={t.body || ""} onChange={(e)=>set("body", e.target.value)} rows={3}
          className="mt-1 bg-background border-surface-3 rounded-none text-sm" data-testid="theme-body" />
      </div>
      <div className="mt-3">
        <div className="text-[10px] font-condensed uppercase tracking-widest text-slate-500">Reflection Question</div>
        <Input value={t.reflection || ""} onChange={(e)=>set("reflection", e.target.value)}
          className="mt-1 bg-background border-surface-3 rounded-none text-sm" />
      </div>
      <div className="mt-4 flex items-center justify-between">
        {t.id ? (
          <Button variant="ghost" onClick={del} className="rounded-none hover:text-red-400 h-9 text-[11px] font-condensed uppercase tracking-widest">
            <Trash2 className="w-3 h-3 mr-1" /> Delete
          </Button>
        ) : <div />}
        <Button onClick={save} disabled={saving} data-testid="theme-save"
          className="rounded-none bg-amber hover:bg-amber-600 text-[#0B0D12] h-9 text-[11px] font-condensed uppercase tracking-widest">
          <Save className="w-3 h-3 mr-1" /> {saving ? "Saving…" : "Save Theme"}
        </Button>
      </div>
    </div>
  );
}

function ThemesPanel() {
  const [items, setItems] = useState([]);
  const [adding, setAdding] = useState(false);

  const reload = async () => {
    const { data } = await api.get("/culture/themes");
    // Convert "__none__" placeholder back — not needed since we store empty
    setItems(data);
  };
  useEffect(() => { reload(); }, []);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <CalendarDays className="w-4 h-4 text-amber" />
          <div className="font-condensed uppercase tracking-[0.25em] text-sm">Weekly Themes</div>
          <div className="text-[10px] font-condensed uppercase tracking-widest text-slate-500 ml-3">{items.length} weeks planned</div>
        </div>
        {!adding && (
          <Button onClick={() => setAdding(true)} data-testid="new-theme-btn"
            className="rounded-none bg-amber hover:bg-amber-600 text-[#0B0D12] h-9 text-[11px] font-condensed uppercase tracking-widest">
            <Plus className="w-3 h-3 mr-1" /> New Theme
          </Button>
        )}
      </div>
      {adding && (
        <ThemeEditor
          theme={{ id: null, week: (items[items.length - 1]?.week || 0) + 1, title: "", body: "", value_ref: "", reflection: "" }}
          onSaved={(t) => { setItems([...items, t].sort((a,b)=>a.week-b.week)); setAdding(false); }}
          onDeleted={() => setAdding(false)}
        />
      )}
      {items.map(t => (
        <ThemeEditor
          key={t.id}
          theme={t}
          onSaved={(nt) => setItems(items.map(x => x.id === nt.id ? nt : x))}
          onDeleted={(id) => setItems(items.filter(x => x.id !== id))}
        />
      ))}
      {items.length === 0 && !adding && <div className="text-sm text-slate-500 italic">No themes yet — plan out the season.</div>}
    </div>
  );
}

// -------------------- Lessons --------------------
function LessonEditor({ lesson, onSaved, onDeleted }) {
  const [l, setL] = useState(lesson);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setL(prev => ({ ...prev, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        title: l.title, content: l.content || "",
        value_ref: (l.value_ref === "__none__" ? "" : (l.value_ref || "")),
        duration_min: +l.duration_min || 10,
      };
      const { data } = l.id ? await api.put(`/culture/lessons/${l.id}`, payload) : await api.post("/culture/lessons", payload);
      onSaved(data);
      toast.success("Lesson saved");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };
  const del = async () => {
    if (!l.id || !window.confirm("Delete this lesson?")) return;
    await api.delete(`/culture/lessons/${l.id}`);
    onDeleted(l.id);
    toast.success("Deleted");
  };
  return (
    <div className="bg-surface border border-surface-3 p-5" data-testid={`lesson-editor-${l.id || 'draft'}`}>
      <div className="grid grid-cols-[1fr_180px_120px] gap-3">
        <div>
          <div className="text-[10px] font-condensed uppercase tracking-widest text-slate-500">Lesson Title</div>
          <Input value={l.title || ""} onChange={(e)=>set("title", e.target.value)}
            className="mt-1 bg-background border-surface-3 rounded-none" data-testid="lesson-title" />
        </div>
        <div>
          <div className="text-[10px] font-condensed uppercase tracking-widest text-slate-500">Value Tie</div>
          <Select value={l.value_ref || ""} onValueChange={(v)=>set("value_ref", v)}>
            <SelectTrigger className="mt-1 bg-background border-surface-3 rounded-none"><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent className="bg-surface border-surface-3 rounded-none">
              {VALUE_OPTIONS.map(o => <SelectItem key={o.v} value={o.v || "__none__"}>{o.l}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <div className="text-[10px] font-condensed uppercase tracking-widest text-slate-500">Duration (min)</div>
          <Input type="number" value={l.duration_min || 10} onChange={(e)=>set("duration_min", +e.target.value || 10)}
            className="mt-1 bg-background border-surface-3 rounded-none text-center font-mono" />
        </div>
      </div>
      <div className="mt-3">
        <div className="text-[10px] font-condensed uppercase tracking-widest text-slate-500">Lesson Content</div>
        <Textarea value={l.content || ""} onChange={(e)=>set("content", e.target.value)} rows={6}
          className="mt-1 bg-background border-surface-3 rounded-none text-sm leading-relaxed" data-testid="lesson-content" />
      </div>
      <div className="mt-4 flex items-center justify-between">
        {l.id ? (
          <Button variant="ghost" onClick={del} className="rounded-none hover:text-red-400 h-9 text-[11px] font-condensed uppercase tracking-widest">
            <Trash2 className="w-3 h-3 mr-1" /> Delete
          </Button>
        ) : <div />}
        <Button onClick={save} disabled={saving} data-testid="lesson-save"
          className="rounded-none bg-amber hover:bg-amber-600 text-[#0B0D12] h-9 text-[11px] font-condensed uppercase tracking-widest">
          <Save className="w-3 h-3 mr-1" /> {saving ? "Saving…" : "Save Lesson"}
        </Button>
      </div>
    </div>
  );
}

function LessonsPanel() {
  const [items, setItems] = useState([]);
  const [adding, setAdding] = useState(false);
  const reload = async () => {
    const { data } = await api.get("/culture/lessons");
    setItems(data);
  };
  useEffect(() => { reload(); }, []);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <GraduationCap className="w-4 h-4 text-amber" />
          <div className="font-condensed uppercase tracking-[0.25em] text-sm">Leadership & Character Curriculum</div>
        </div>
        {!adding && (
          <Button onClick={() => setAdding(true)} data-testid="new-lesson-btn"
            className="rounded-none bg-amber hover:bg-amber-600 text-[#0B0D12] h-9 text-[11px] font-condensed uppercase tracking-widest">
            <Plus className="w-3 h-3 mr-1" /> New Lesson
          </Button>
        )}
      </div>
      {adding && (
        <LessonEditor
          lesson={{ id: null, title: "", content: "", value_ref: "", duration_min: 10 }}
          onSaved={(l) => { setItems([l, ...items]); setAdding(false); }}
          onDeleted={() => setAdding(false)}
        />
      )}
      {items.map(l => (
        <LessonEditor
          key={l.id}
          lesson={l}
          onSaved={(nl) => setItems(items.map(x => x.id === nl.id ? nl : x))}
          onDeleted={(id) => setItems(items.filter(x => x.id !== id))}
        />
      ))}
      {items.length === 0 && !adding && <div className="text-sm text-slate-500 italic">No lessons yet.</div>}
    </div>
  );
}

// -------------------- Main page --------------------
export default function CultureBuilder() {
  return (
    <div data-testid="culture-page">
      <TopBar title="Culture Builder" subtitle="Build culture on purpose. Tied to your core values." />
      <div className="p-8 max-w-6xl">
        <Tabs defaultValue="quotes" className="w-full">
          <TabsList className="bg-surface border border-surface-3 rounded-none p-1 h-auto">
            <TabsTrigger value="quotes" data-testid="tab-quotes"
              className="rounded-none data-[state=active]:bg-amber data-[state=active]:text-[#0B0D12] font-condensed uppercase tracking-widest text-[11px] px-4 py-2">
              <Quote className="w-3 h-3 mr-1.5" /> Quote Library
            </TabsTrigger>
            <TabsTrigger value="themes" data-testid="tab-themes"
              className="rounded-none data-[state=active]:bg-amber data-[state=active]:text-[#0B0D12] font-condensed uppercase tracking-widest text-[11px] px-4 py-2">
              <CalendarDays className="w-3 h-3 mr-1.5" /> Weekly Themes
            </TabsTrigger>
            <TabsTrigger value="lessons" data-testid="tab-lessons"
              className="rounded-none data-[state=active]:bg-amber data-[state=active]:text-[#0B0D12] font-condensed uppercase tracking-widest text-[11px] px-4 py-2">
              <GraduationCap className="w-3 h-3 mr-1.5" /> Leadership Curriculum
            </TabsTrigger>
          </TabsList>
          <TabsContent value="quotes" className="mt-6"><QuotesPanel /></TabsContent>
          <TabsContent value="themes" className="mt-6"><ThemesPanel /></TabsContent>
          <TabsContent value="lessons" className="mt-6"><LessonsPanel /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
